<template>
  <div
    v-if="src || fallback"
    class="m-avatar"
    :class="{
      [`m-avatar--${size}`]: size,
      [`m-avatar--responsive`]: responsive,
    }"
  >
    <slot>
      <img
        v-if="src"
        :src="src"
        :alt="alt"
        loading="lazy"
        width="48"
        height="48"
      />
    </slot>
    <MIcon
      v-if="fallback && !src && !$slots.default"
      icon="user"
      size="base"
      :title="alt"
    />
  </div>
</template>

<script>
import MIcon from '../../typography/Icon/MIcon'
export default {
  name: 'MAvatar',
  components: {
    MIcon,
  },
  props: {
    /**
     * The path of the image.
     */
    src: {
      type: String,
      default: '',
    },
    /**
     * The alt text for the image.
     * Can be left blank if there is no semantic value to the image.
     */
    alt: {
      type: String,
      default: '',
    },
    /**
     * Display a default avatar image if no src image is provided.
     */
    fallback: {
      type: Boolean,
      default: true,
    },
    /**
     * Sets responsive sizing for the avatar.
     */
    responsive: {
      type: Boolean,
      default: false,
    },
    /**
     * Set the size of the avatar.
     */
    size: {
      type: String,
      default: 'medium',
      validator: (prop) => ['small', 'medium', 'large'].includes(prop),
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/breakpoint';
@use '../../../sass/framework/mixins/font-size';

.m-avatar {
  $avatar-size: calc(var(--typography-paragraph-regular-line-height) * 2rem);
  align-items: center;
  background: var(--skin-background-mid);
  border-radius: 50%;
  display: flex;
  font-size: calc(var(--typography-paragraph-regular-line-height) * 1rem);
  height: $avatar-size;
  justify-content: center;
  overflow: hidden;
  width: $avatar-size;

  :deep(img) {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  &--small {
    @include font-size.font-size(lg);
    height: calc(var(--typography-paragraph-regular-line-height) * 1.5rem);
    width: calc(var(--typography-paragraph-regular-line-height) * 1.5rem);

    &.m-avatar--responsive {
      @include breakpoint.breakpoint(tablet-up) {
        height: calc(var(--typography-paragraph-regular-line-height) * 3rem);
        width: calc(var(--typography-paragraph-regular-line-height) * 3rem);
      }
    }
  }

  &--medium {
    height: calc(var(--typography-paragraph-regular-line-height) * 3rem);
    width: calc(var(--typography-paragraph-regular-line-height) * 3rem);

    &.m-avatar--responsive {
      @include breakpoint.breakpoint(tablet-up) {
        height: calc(var(--typography-paragraph-regular-line-height) * 3.33rem);
        width: calc(var(--typography-paragraph-regular-line-height) * 3.33rem);
      }
    }
  }

  &--large {
    height: calc(var(--typography-paragraph-regular-line-height) * 3.33rem);
    width: calc(var(--typography-paragraph-regular-line-height) * 3.33rem);

    &.m-avatar--responsive {
      @include breakpoint.breakpoint(tablet-up) {
        height: calc(var(--typography-paragraph-regular-line-height) * 5.16rem);
        width: calc(var(--typography-paragraph-regular-line-height) * 5.16rem);
      }
    }
  }
}
</style>
